import { FC, useCallback, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import {
  getOrg,
  getRegion,
  getRegionArea,
  patchOrgAdmin,
  patchRegion,
  deleteRegion,
  listSubregion,
  RegionWithSubregions,
  ResolutionTier,
  SubregionModel,
} from "@rubi/api-generated";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Crumb, DevPage, ErrorMsg, Section } from "./common";
import {
  ProcessingRunLink,
  RegionAreaMapSection,
  RegionBreadcrumbs,
} from "./regionShared";

export const DevRegionPage: FC = () => {
  const { orgId = "", regionId = "" } = useParams();
  const navigate = useNavigate();

  const [region, setRegion] = useState<RegionWithSubregions | null>(null);
  const [areaVersion, setAreaVersion] = useState(0);
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [candidateSubregions, setCandidateSubregions] = useState<
    SubregionModel[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const isActive = !!region && region.id === activeRegionId;

  const load = async () => {
    try {
      const [regionResponse, orgResponse] = await Promise.all([
        getRegion({ path: { org_id: orgId, region_id: regionId } }),
        getOrg({ path: { org_id: orgId } }),
      ]);
      const r = regionResponse.data ?? null;
      setRegion(r);
      setActiveRegionId(orgResponse.data?.region_id ?? null);
      if (r) {
        const candidatesResponse = await listSubregion({
          path: { org_id: orgId },
          query: {
            geo_data_file_processing_run_id: r.geo_data_file_processing_run_id,
          },
        });
        setCandidateSubregions(candidatesResponse.data?.subregions ?? []);
        const subregion_ids = r.subregions.map((subregion) => subregion.id);
        setSelectedIds(new Set(subregion_ids));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load Region");
    }
  };

  useEffect(() => {
    load();
  }, [orgId, regionId]);

  const fetchArea = useCallback(
    async (tier: ResolutionTier) =>
      (
        await getRegionArea({
          path: { org_id: orgId, region_id: regionId, tier },
        })
      ).data,
    [orgId, regionId],
  );

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const membershipDirty =
    region &&
    (selectedIds.size !== region.subregions.length ||
      region.subregions.some((subregion) => !selectedIds.has(subregion.id)));

  const handleSaveMembership = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await patchRegion({
        path: { org_id: orgId, region_id: regionId },
        body: { subregion_ids: [...selectedIds] },
      });
      // The backend regenerates the region's area rows on a membership
      // change — re-fetch everything rather than patch state locally, so the
      // map doesn't keep showing the boundary from before the save.
      await load();
      setAreaVersion((v) => v + 1);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setActionBusy(false);
    }
  };

  const handleEnable = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await patchOrgAdmin({
        path: { org_id: orgId },
        body: { region_id: regionId },
      });
      setActiveRegionId(regionId);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to enable");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDisable = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await patchOrgAdmin({
        path: { org_id: orgId },
        body: { region_id: null },
      });
      setActiveRegionId(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to disable");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete Region ${regionId}? This cannot be undone.`)) {
      return;
    }
    setActionBusy(true);
    setActionError(null);
    try {
      await deleteRegion({ path: { org_id: orgId, region_id: regionId } });
      navigate(`/dev_admin/region/${orgId}/region`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to delete");
      setActionBusy(false);
    }
  };

  return (
    <DevPage
      title={`Region ${regionId}`}
      breadcrumbs={
        <RegionBreadcrumbs orgId={orgId}>
          <Crumb>
            <Link to={`/dev_admin/region/${orgId}/region`}>Regions</Link>
          </Crumb>
          <Crumb>{regionId}</Crumb>
        </RegionBreadcrumbs>
      }
    >
      <Section subtitle="Details">
        <ErrorMsg msg={error} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>id</td>
              <td className="mono">{region?.id}</td>
            </tr>
            <tr>
              <td>processing run</td>
              <td className="mono">
                <ProcessingRunLink
                  orgId={orgId}
                  runId={region?.geo_data_file_processing_run_id}
                />
              </td>
            </tr>
            <tr>
              <td>status</td>
              <td>
                {isActive ? (
                  <Chip size="small" color="success" label="Active for org" />
                ) : (
                  <span className="muted">not active</span>
                )}
              </td>
            </tr>
            <tr>
              <td>time_created</td>
              <td className="mono muted">{region?.time_created}</td>
            </tr>
          </tbody>
        </table>

        {actionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionError}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {isActive ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleDisable}
              disabled={actionBusy}
            >
              Disable in org
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleEnable}
              disabled={actionBusy}
            >
              Enable in org
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={actionBusy || isActive}
          >
            Delete region
          </Button>
        </Stack>
        {isActive && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This Region is active for the org — membership and deletion are
            locked until it's disabled.
          </Typography>
        )}
      </Section>

      <Section subtitle="Member Subregions">
        <table className="dev-table">
          <thead>
            <tr>
              <th style={{ width: 1 }}></th>
              <th>id</th>
              <th>metadata</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {candidateSubregions.map((sr) => (
              <tr key={sr.id}>
                <td>
                  <Checkbox
                    checked={selectedIds.has(sr.id)}
                    disabled={isActive}
                    onChange={() => toggleOne(sr.id)}
                  />
                </td>
                <td className="mono">{sr.id}</td>
                <td className="mono">{JSON.stringify(sr.metadata)}</td>
                <td style={{ textAlign: "right" }}>
                  <Link to={`/dev_admin/region/${orgId}/subregion/${sr.id}`}>
                    view
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isActive && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              disabled={!membershipDirty || actionBusy}
              onClick={handleSaveMembership}
            >
              Save membership
            </Button>
          </Stack>
        )}
      </Section>

      <RegionAreaMapSection fetchArea={fetchArea} reloadKey={areaVersion} />
    </DevPage>
  );
};
